import React, { useState, useEffect, useRef } from 'react';
import { useAgentSession } from '../../contexts/AgentSessionContext';
import { MessageBubble } from './MessageBubble';
import { ToolExecutionPill } from './ToolExecutionPill';
import { ChatInput } from './ChatInput';
import { Bot, TrendingUp, AlertTriangle, Layers, Key } from 'lucide-react';
import { ApiKeyModal } from '../layout/ApiKeyModal';

export const ChatWindow: React.FC = () => {
  const { messages, activeToolCalls, isStreaming, error } = useAgentSession();
  const [apiKeyModalOpen, setApiKeyModalOpen] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);
  const backendEndpoint =
    import.meta.env.VITE_BACKEND_AGENT_ENDPOINT || 'https://finadv-agent-api.adhyaconsulting.org';

  // Follow the stream only while the user is at the bottom, so scrolling back to read isn't yanked away.
  // Scrolling the container (not scrollIntoView) avoids iOS shifting the whole page under the keyboard.
  const handleScroll = () => {
    const el = scrollAreaRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  useEffect(() => {
    const el = scrollAreaRef.current;
    if (el && stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, activeToolCalls, isStreaming]);

  // A newly sent user message always brings the view back to the bottom.
  useEffect(() => {
    if (messages[messages.length - 1]?.role === 'user') {
      stickToBottomRef.current = true;
    }
  }, [messages]);

  return (
    <div className="chat-window-layout">
      <div
        className="messages-scroll-area"
        ref={scrollAreaRef}
        onScroll={handleScroll}
      >
        {messages.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon-circle">
              <Bot size={36} className="text-accent" />
            </div>
            <h2>Welcome to Finance Advisor Agent</h2>
            <p className="empty-description">
              Powered by Google ADK Java multi-agent orchestrator. Ask questions about your portfolio, calculate deterministic PnL, research market catalysts, or model forward-looking stress scenarios.
            </p>

            <div className="welcome-cards-grid">
              <div className="welcome-card">
                <TrendingUp size={20} className="welcome-card-icon text-green" />
                <h3>Deterministic Math</h3>
                <p>PnL, weighted allocations & concentration risk calculated in Java without LLM hallucinations.</p>
              </div>
              <div className="welcome-card">
                <Layers size={20} className="welcome-card-icon text-blue" />
                <h3>Multi-Turn Session Memory</h3>
                <p>Loads holdings once from SQLite/Firestore and remembers context across subsequent queries.</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="messages-list">
            {messages.map((msg) => (
              <MessageBubble key={msg.id} message={msg} />
            ))}

            {/* Render active tool executions */}
            {activeToolCalls.length > 0 && (
              <div className="tool-executions-container">
                <span className="tool-stream-label">Agent Tool Telemetry:</span>
                <div className="tool-pills-row">
                  {activeToolCalls.map((tool) => (
                    <ToolExecutionPill key={tool.id} tool={tool} />
                  ))}
                </div>
              </div>
            )}

            {isStreaming && (
              <div className="typing-indicator-row">
                <div className="typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span className="typing-label">Finance Advisor is thinking & calculating...</span>
              </div>
            )}

            {error && (
              <div className="chat-error-banner">
                <AlertTriangle size={18} className="text-red" />
                <div style={{ flex: 1 }}>
                  <div>
                    <strong>Error:</strong> {error}
                  </div>
                  {(error.includes('401') || error.toLowerCase().includes('api-key') || error.toLowerCase().includes('api key')) && (
                    <div style={{ marginTop: '10px' }}>
                      <button
                        type="button"
                        onClick={() => setApiKeyModalOpen(true)}
                        className="btn-configure-key"
                        style={{
                          background: 'var(--accent)',
                          border: 'none',
                          color: '#ffffff',
                          padding: '6px 14px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          boxShadow: 'var(--shadow-sm)',
                        }}
                      >
                        <Key size={14} /> Configure API Key
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <ChatInput />

      <ApiKeyModal
        isOpen={apiKeyModalOpen}
        onClose={() => setApiKeyModalOpen(false)}
        endpoint={backendEndpoint}
      />
    </div>
  );
};
